"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { getHeaderDensity } from "./headerDensity";
import type { HeaderLink } from "./headerModel";

interface SecondaryMenuBarProps {
    items: HeaderLink[];
    basePath: string;
    background?: string;
    textMode?: "light" | "dark";
    density?: string;
    styleOverrides?: React.CSSProperties;
}

export function SecondaryMenuBar({
    items,
    basePath,
    background,
    textMode = "dark",
    density,
    styleOverrides = {},
}: SecondaryMenuBarProps) {
    const d = getHeaderDensity(density);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        checkScroll();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener("scroll", checkScroll, { passive: true });
        const ro = new ResizeObserver(checkScroll);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", checkScroll);
            ro.disconnect();
        };
    }, [checkScroll]);

    const scroll = (direction: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction === "right" ? 200 : -200, behavior: "smooth" });
    };

    if (items.length === 0) return null;

    const isLight = textMode === "light";
    const chevronColorClass = isLight ? "text-white/60" : "text-muted-foreground";
    const borderClass = isLight ? "border-white/10" : "border-border";

    // Fade edges: extract the first solid color from the background for the gradient fade
    const bgString = background || "#f1f5f9";
    // Try to extract the last color from the gradient for the fade edge
    const colorMatch = bgString.match(/#[0-9a-fA-F]{3,8}/g);
    const fadeColor = colorMatch ? colorMatch[colorMatch.length - 1] : "#f1f5f9";

    return (
        <div
            className={`relative border-b z-0 ${borderClass} secondary-header-surface`}
            style={{ background: bgString, ...styleOverrides }}
        >
            <div className="max-w-350 mx-auto">
                {/* Left fade + arrow */}
                {canScrollLeft && (
                    <button
                        onClick={() => scroll("left")}
                        className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-2 pr-4 cursor-pointer"
                        style={{
                            background: `linear-gradient(to right, ${fadeColor}, ${fadeColor}E6, transparent)`,
                        }}
                        aria-label="Scroll left"
                    >
                        <ChevronLeft className={`w-4 h-4 ${chevronColorClass}`} />
                    </button>
                )}

                {/* Scrollable items */}
                <div
                    ref={scrollRef}
                    className={`flex items-center ${d.secondaryGap} px-4 ${d.secondaryPy} overflow-x-auto scrollbar-hide`}
                >
                    {items.map((item, index) => (
                        <Link
                            key={index}
                            href={item.url.startsWith("http") ? item.url : `${basePath}${item.url}`}
                            prefetch={false}
                            target={item.openInNewTab ? "_blank" : undefined}
                            className={`${d.textSize} font-medium whitespace-nowrap shrink-0 transition opacity-80 hover:opacity-100`}
                        >
                            {item.label}
                        </Link>
                    ))}
                </div>

                {/* Right fade + arrow */}
                {canScrollRight && (
                    <button
                        onClick={() => scroll("right")}
                        className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-2 pl-4 cursor-pointer"
                        style={{
                            background: `linear-gradient(to left, ${fadeColor}, ${fadeColor}E6, transparent)`,
                        }}
                        aria-label="Scroll right"
                    >
                        <ChevronRight className={`w-4 h-4 ${chevronColorClass}`} />
                    </button>
                )}
            </div>
        </div>
    );
}
